import {
  EmailSchema,
  PagingSchema,
  PushSchema,
  SmsSchema,
  createDetailResponseSchema,
  createListResponseSchema,
  createListResponseSchemaDefault,
} from 'src/feature/bite/domain'
import z from 'zod'

export const SegmentSchema = z.object({
  macroSegment: z.string(),
  microSegment: z.string(),
  demographicInfos: z.any(),
  behavioralSegments: z.any(),
})

export const AddressSchema = z.object({
  buildingNo: z.string(),
  zipCode: z.string(),
  region: z.string(),
  city: z.string(),
  country: z.string(),
  addressLine: z.string(),
})

export const ConsentSchema = z.object({
  termsAndConditions: z.boolean().optional(),
  email: z.boolean(),
  sms: z.boolean(),
  push: z.boolean(),
})

export const TransactionSchema = z.object({
  orderId: z.string().nullable(),
  transactionId: z.string().nullable(),
  date: z.string(),
  type: z.any(),
  paidAmount: z.any(),
  totalAmount: z.any(),
  discountAmount: z.any(),
  channel: z.any(),
  posNo: z.string().nullable(),
  storeNo: z.string().nullable(),
})

export const ProviderSchema = z.object({
  middleName: z.string().nullable(),
  surname: z.string(),
  totalEarnings: z.number(),
  gender: z.number(),
  birthDate: z.string().nullable(),
  customerToken: z.string(),
  externalMemberId: z.string(),
  preferredLanguage: z.string(),
  secondLanguage: z.string(),
  memberId: z.string(),
  name: z.string(),
  netPoints: z.number(),
  worth: z.number(),
  savings: z.number(),
  mobile: z.string().nullable(),
  email: z.string().nullable(),
  enrollmentDate: z.string(),
  status: z.number(),
  storeName: z.string(),
  storeId: z.string(),
  customerGroup: z.string(),
})

export const ProviderDetailSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  status: z.number().nullable().optional(),
  bankName: z.string().nullable().optional(),
  birthDate: z.string().nullable().optional(),
  certifications: z.string().nullable().optional(),
  certificates: z.string().nullable().optional(),
  cityID: z.string().nullable().optional(),
  commissionAmount: z.number().nullable().optional(),
  commissionRate: z.number().nullable().optional(),
  commissionType: z.number().nullable().optional(),
  contactName: z.string().nullable().optional(),
  contractURL: z.string().nullable().optional(),
  contactSurname: z.string().nullable().optional(),
  contactPhone: z.string().nullable().optional(),
  termsAndConditionsEnabled: z.boolean().nullable().optional(),
  contactEmail: z.string().nullable().optional(),
  conferencesAttended: z.string().nullable().optional(),
  merchantID: z.string().nullable().optional(),
  countryID: z.string().nullable().optional(),
  countyID: z.string().nullable().optional(),
  webSite: z.string().nullable().optional(),
  infoContentData: z.any().nullable().optional(),
  createdOn: z.string().nullable().optional(),
  education: z.string().nullable().optional(),
  qualifications: z.string().nullable().optional(),
  experience: z.string().nullable().optional(),
  memberships: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  enrollmentFee: z.number().nullable().optional(),
  enrollmentFeeType: z.number().nullable().optional(),
  facebook: z.string().nullable().optional(),
  gender: z.number().nullable().optional(),
  groupClassID: z.string().nullable().optional(),
  iban: z.string().nullable().optional(),
  imagesData: z.any().nullable().optional(),
  instagram: z.string().nullable().optional(),
  invoiceAddress: z.string().nullable().optional(),
  linkedIn: z.string().nullable().optional(),
  middleName: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  onlineClass: z.number().nullable().optional(),
  partnerID: z.string().nullable().optional(),
  partnerTypeID: z.string().nullable().optional(),
  paymentFrequency: z.number().nullable().optional(),
  payoutDate: z.number().nullable().optional(),
  payoutMethod: z.number().nullable().optional(),
  philosophy: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  regionId: z.string().nullable().optional(),
  skillList: z.any().nullable().optional(),
  skills: z.any().nullable().optional(),
  specialty: z.string().nullable().optional(),
  stateID: z.string().nullable().optional(),
  surname: z.string().nullable().optional(),
  taxNumber: z.string().nullable().optional(),
  taxOffice: z.string().nullable().optional(),
  categoryIDs: z.array(z.string()).nullable(),
  subCategoryIDs: z.array(z.string()).nullable(),
  categoryName: z.string().nullable().optional(),
  subCategoryName: z.string().nullable().optional(),
  mccCode: z.string().nullable().optional(),
  slipSlogan: z.string().nullable().optional(),
  slipName: z.string().nullable().optional(),
  vascoAppDisplayName: z.string().nullable().optional(),
  groupClass: z.number().nullable().optional(),
  twitter: z.string().nullable().optional(),
  contractDate: z.string().nullable().optional(),
  contractDocument: z.any().nullable().optional(),
  contractDuration: z.any().nullable().optional(),
  onboardingDate: z.string().nullable().optional(),
  officialName: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  workingHourList: z.any().nullable().optional(),
  additionalInformations: z.string().nullable().optional(),
  financialTaxOffice: z.string().nullable().optional(),
  financialIBAN: z.string().nullable().optional(),
  financialTaxNumber: z.string().nullable().optional(),
  financialInvoiceAddress: z.string().nullable().optional(),
  financialBankName: z.string().nullable().optional(),
  financialOfficialName: z.string().nullable().optional(),
  financialCurrency: z.number().nullable().optional(),
  financialPayoutDate: z.number().nullable().optional(),
  financialPayoutMethod: z.number().nullable().optional(),
  financialPaymentFrequency: z.number().nullable().optional(),
})

export const ProviderTabSchema = z.object({
  Consent: ConsentSchema.optional(),
  Segment: SegmentSchema.optional(),
  Address: z.object({dataList: AddressSchema.array(), paging: PagingSchema}).optional(),
  Transaction: z.object({dataList: TransactionSchema.array(), paging: PagingSchema}).optional(),
  // Engagement
  SmsEngagement: z.object({dataList: SmsSchema.array(), paging: PagingSchema}).optional(),
  PushEngagement: z.object({dataList: PushSchema.array(), paging: PagingSchema}).optional(),
  EmailEngagement: z.object({dataList: EmailSchema.array(), paging: PagingSchema}).optional(),
})

export const Responses = {
  Segment: createDetailResponseSchema(SegmentSchema),
  // Address: createDetailResponseSchema(z.object({ addresses: AddressSchema.array() })),
  Address: createListResponseSchema(AddressSchema.array()),
  Consent: createDetailResponseSchema(ConsentSchema),
  Transaction: createListResponseSchema(TransactionSchema.array()),
  SmsEngagement: createListResponseSchemaDefault(
    z.object({
      emails: EmailSchema.array(),
      pushes: PushSchema.array(),
      textMessages: SmsSchema.array(),
      listPaging: PagingSchema,
    })
  ),
  PushEngagement: createListResponseSchemaDefault(
    z.object({
      emails: EmailSchema.array(),
      pushes: PushSchema.array(),
      textMessages: SmsSchema.array(),
      listPaging: PagingSchema,
    })
  ),
  EmailEngagement: createListResponseSchemaDefault(
    z.object({
      emails: EmailSchema.array(),
      pushes: PushSchema.array(),
      textMessages: SmsSchema.array(),
      listPaging: PagingSchema,
    })
  ),
}

export const ProviderSchemaResponse = createDetailResponseSchema(ProviderDetailSchema)

export type ProviderTab = z.infer<typeof ProviderTabSchema>

export type Provider = z.infer<typeof ProviderSchema>
export type ProviderDetail = z.infer<typeof ProviderDetailSchema>
export type ProviderResponse = z.infer<typeof ProviderSchemaResponse>
